<template>
    <slot />

    <ChaseReveals ref="refReveals" @revealComplete="onRevealComplete" v-if="isChaseTier" />
    <GrailReveals
        ref="refReveals"
        v-if="isGrailTier"
        @revealComplete="onRevealComplete"
        :category="category"
    />
    <CardDetails ref="refCardDetails" v-if="data.detailText" v-bind="data.detailText" />
</template>

<script>
    /*     export const TRANSITIONS_REVEAL = {
        SPIN: 'spin',
        ZOOM: 'zoom',
        TILT: 'tilt',
        WIGGLE: 'wiggle',
    }; */
</script>

<script setup>
    import { BLOOM_INTENSITY, useBloom, useCursor, useWindowMessage } from '#imports';
    import {
        CardRevealComplete,
        StepCardsReveal,
        Tier0,
        Tier1,
        Tier2,
        Tier3,
    } from '@fanatics/resn-api/instant-rips';
    import { noop, whenever } from '@vueuse/core';
    import { Vector3 } from 'three';
    import {
        computed,
        nextTick,
        onBeforeUnmount,
        onMounted,
        provide,
        ref,
        watch,
        watchEffect,
    } from 'vue';

    import { map, randomFloat } from '@resn/gozer-math';
    import { disposeObject } from '@resn/gozer-three/helpers';
    import { usePane, useRafBool, useRenderer, useThreeObject } from '@resn/gozer-vue';
    import { gsap } from '@resn/gsap';

    import CardDetails from '~/components/gl/async-experience/CardDetails.vue';
    import { LIGHTS_DOWN_WIPE_ANGLE } from '~/components/gl/Cards/cardRevealConstants';
    import ChaseReveals from '~/components/gl/Cards/reveals/chase/ChaseReveals.vue';
    import GrailReveals from '~/components/gl/Cards/reveals/grail/GrailReveals.vue';
    import { useShineWipe } from '~/components/gl/shaders/ShaderChunks/shine-wipe';
    import { useCardMotion } from '~/composables/useCardMotion';
    import { useRawCard } from '~/composables/useRawCard';
    import {
        getPaletteIdForTier,
        GRAIL_TUNNEL_REVEAL,
        GRAIL_TUNNEL_SET,
        GRAIL_TUNNEL_WIPE,
        LIGHTS_DOWN_DUR,
        RAW_CARD_SCALE,
    } from '~/core/constants';
    import { useAudio } from '~/providers/AudioProvider';
    import { useColorPalette } from '~/providers/ColorPaletteProvider';
    import { useFanatics } from '~/providers/instant-rips/FanaticsProvider';
    import { createLogger } from '~/utils/logger';

    const logger = createLogger('gl.card');

    const props = defineProps({
        index: { default: 0 },
        active: { default: false },
        visible: { default: false },
        rotation: { default: 0 },
        flip: { default: false },
        revealed: { default: false },
        motionActive: { default: false },
        interactive: { default: false },
        activePositionDelay: { default: 0 },

        zoom: { default: true, required: false },
        colorPalette: { default: [] },
        enableInteractionOnReveal: { default: false },
        fastReveal: { default: false },
        skipReveal: { default: false },

        data: {
            default: {
                imageFront: null,
                imageBack: null,
            },
        },
    });

    const BASE_RAW_WIPE_ANGLE = 245;

    const propsTransition = {
        transitionRevealId: null,
    };

    const { clock } = useRenderer();
    const fanatics = useFanatics();
    const { setColorPalette } = useColorPalette();

    const { addCursor, removeCursor } = useCursor();
    const audio = useAudio();
    const bloom = useBloom();
    const refReveals = ref(null);
    const refCardDetails = ref(null);

    const setCardDetailsActive = (val, delay = 0) => {
        refCardDetails.value?.setActive(val, delay);
    };

    const visible = computed(() => props.visible);
    const active = computed(() => props.active);
    const zoomed = computed(() => revealFinal.value && props.zoom);
    const canRotate = computed(() => revealFinal.value);
    const canFlip = computed(() => revealFinal.value && props.flip);

    const isActive = ref(false);
    const inActivePosition = ref(false);

    const isBaseTier = computed(
        () => props.forceBaseTier || props.data.tier === Tier1 || props.data.tier === Tier0
    );
    const isChaseTier = computed(() => props.data.tier === Tier2);
    const isGrailTier = computed(() => props.data.tier === Tier3);
    const isHighTier = computed(() => !isBaseTier.value);
    const paletteId = computed(() => getPaletteIdForTier(props.data.tier));

    const baseRevealPr = computed(() => (isBaseTier.value ? 1 : 0));

    const internalInteractive = ref(false);
    const render = ref(false);

    const revealed = computed(() => (isBaseTier.value ? inActivePosition.value : props.revealed));
    const revealedComplete = ref(false);
    const revealFinal = ref(false);

    const motionActive = computed(() => props.motionActive && !props.interactive);

    const time = ref(0);
    const revealPr = ref(baseRevealPr.value); // Raw cards are always "revealed" - no slab to hide them
    const inProgress = ref(0);
    const depthOffset = ref(0);
    const outlineScale = ref(0.83);
    const heartbeatPr = ref(0);
    const shineZOffset = ref(0);
    const wipeAngle = ref(BASE_RAW_WIPE_ANGLE);

    // Material setup
    const metalness = ref(0.5);
    const roughness = ref(0.05);
    const envMapIntensity = ref(3);
    const envMapRotation = ref(new Vector3());
    const matcapScale = ref(2);
    const category = computed(() => props.data.category || '');

    const { mesh, shaders, shadersAll } = useRawCard({ props });

    const shineWipe = useShineWipe('CardRaw Wipe');

    const { sendMessage } = useWindowMessage();
    const { object, props: propsObject } = useThreeObject(null, {
        name: 'CardRaw',
        props: { v: false, s: RAW_CARD_SCALE },
    });

    const cardMotion = useCardMotion({
        object,
        motionActive,
        interactive: internalInteractive,
    });

    const setupObject = () => {
        cardMotion.finalObject.add(mesh);

        requestAnimationFrame(() => {
            setActive(active.value);
            setVisible(visible.value);
            setRender();
        });
    };

    onMounted(() => {
        setupObject();

        revealPr.value = baseRevealPr.value;

        shaders.forEach((shader) => {
            shader.uniforms.uPreRevealColor1.value.set('#87b2ff');
            shader.uniforms.uPreRevealColor2.value.set('#bd8ef6');
        });
    });

    let delayRender;
    let delayInActive;

    const setActive = (bool) => {
        // Skip if the state hasn't changed
        if (bool === isActive.value) return;
        isActive.value = bool;

        delayRender?.kill();
        delayInActive?.kill();

        if (bool) {
            render.value = true;
            const tier = isBaseTier.value ? props.data.tier : Tier0;
            setColorPalette(tier);

            const bloomIntensity = revealed.value || isBaseTier.value ? 0 : BLOOM_INTENSITY;

            gsap.to(bloom.intensity, {
                value: bloomIntensity,
                duration: 1,
                delay: revealed.value ? 0 : 0.2,
                ease: 'power1.inOut',
            });

            delayInActive = gsap.delayedCall(
                props.activePositionDelay,
                () => (inActivePosition.value = true)
            );

            if (revealed.value) {
                revealFinal.value = true;
                if (props.enableInteractionOnReveal) internalInteractive.value = true;
            }
        } else {
            internalInteractive.value = false;

            // Without immediate hiding, a black card placeholder becomes visible
            if (fanatics?.step?.value !== StepCardsReveal) render.value = false;
            else delayRender = gsap.delayedCall(1, () => (render.value = false));

            bloom.reset();
        }
    };

    const setVisible = () => {
        object.visible = visible.value;
        render.value = visible.value;

        onUpdate({ timestamp: 0 });
    };

    const fadeIn = ({
        value = 1,
        force = false,
        delay = 0.2,
        duration = 0.5,
        ease = 'power1.inOut',
    } = {}) => {
        if (force) {
            inProgress.value = value;
            return;
        }

        gsap.killTweensOf(inProgress);
        gsap.to(inProgress, { value, duration, delay, ease });
    };

    const fadeOut = ({ value = 0, duration = 0.1, ease = 'power1.inOut' } = {}) => {
        gsap.killTweensOf(inProgress);
        gsap.to(inProgress, { value, duration, ease });
    };

    const setInProgress = (val) => {
        inProgress.value = val;
    };

    let tlShow;
    const show = ({ delay = 0, revealTransitionId = propsTransition.transitionRevealId } = {}) => {
        propsTransition.transitionRevealId = null;
        // propsTransition.transitionRevealId = getRevealTransitionId({ id: revealTransitionId });

        tlShow?.kill();
        tlShow = gsap
            .timeline({ delay })
            .add(() => (revealFinal.value = true), 0.5)
            .to(inProgress, { value: 1, duration: 1, ease: 'power1.out' }, 0);
        // .fromTo(inProgress, { value: 0.25 }, { value: 1, duration: 1, ease: 'power1.out' }, 0);
    };

    const hide = () => {
        tlShow?.kill();
        cardMotion.hide();
    };

    let tlReveal, tlRevealComplete;
    const setRevealed = (bool) => {
        tlReveal?.kill();

        logger.debug('CardRaw revealed state changed', { revealed: bool });

        if (bool) {
            if (props.skipReveal) {
                revealFinal.value = true;
                revealPr.value = 1;
                return;
            }

            if (active.value) {
                emit('revealStart');

                setColorPalette(props.data.tier);

                const showReveals = refReveals.value && isHighTier.value;

                if (showReveals) playRevealAnimations();
                else onRevealComplete();
            } else revealPr.value = baseRevealPr.value;
        } else {
            if (active.value) setColorPalette(null);

            internalInteractive.value = false;
            revealFinal.value = false;
            revealedComplete.value = false;
            revealPr.value = baseRevealPr.value;

            cardMotion.reset();
        }
    };

    const playRevealAnimations = () => {
        tlReveal = gsap
            .timeline()
            .to(revealPr, { value: 0, duration: 0.5 }, 0)
            .to(bloom.intensity, { value: BLOOM_INTENSITY, duration: 0.5 }, 0);

        if (props.fastReveal) {
            refReveals.value?.show({ fast: true });

            return;
        }

        if (isChaseTier.value) {
            cardMotion.playChaseEffect();
            refReveals.value?.show();
        }

        if (isGrailTier.value) {
            const type = refReveals.value?.show();

            fanatics.fgDimmed.value = type !== 'category';

            let setDelay = GRAIL_TUNNEL_SET;
            let revealDelay;
            let wipeDelay;
            let useWipeReveal = true;

            switch (type) {
                case 'grail-tunnel':
                    revealDelay = GRAIL_TUNNEL_REVEAL;
                    wipeDelay = GRAIL_TUNNEL_WIPE + 0.1;
                    cardMotion.playGrailTunnelEffect();
                    useWipeReveal = false;
                    break;

                case 'lights-down':
                    revealDelay = 4;
                    wipeDelay = revealDelay + 0.5;
                    cardMotion.startLightsDownReveal({ setDelay, revealDelay });
                    break;

                case 'category':
                    revealDelay = 3.75;
                    wipeDelay = revealDelay + 0.5;
                    cardMotion.startLightsDownReveal({ setDelay, revealDelay });
                    break;
            }

            tlReveal
                .add(() => (wipeAngle.value = LIGHTS_DOWN_WIPE_ANGLE), setDelay)
                .to(bloom.intensity, { value: 0, duration: 1.5, ease: 'sine.inOut' }, wipeDelay);

            if (useWipeReveal)
                tlReveal.to(revealPr, { value: 1, duration: 5, ease: 'power2.out' }, wipeDelay);

            tlReveal.add(noop, LIGHTS_DOWN_DUR);
        }
    };

    const onRevealComplete = (ignoreTransition = false) => {
        logger.debug('CardRaw reveal complete');

        const tl = (tlRevealComplete = gsap
            .timeline({
                onComplete: () => {
                    sendMessage(CardRevealComplete, props.index);
                    fanatics.events.emit(CardRevealComplete, props.index);

                    revealedComplete.value = true;
                    propsTransition.transitionRevealId = null;

                    bloom.reset();
                    emit('revealComplete');
                },
            })
            .add(() => (revealFinal.value = true), 0.25));

        if (isHighTier.value) {
            const delayReveal = 0.2;
            const { uShineWipePr, uShineWipeIntensity } = shineWipe.refs;
            uShineWipeIntensity.value = 1.5;

            tl.add(() => audio.play('cardReveal'), 0.1);
            tl.to(bloom.intensity, { value: 0, duration: 0.75, ease: 'sine.inOut' }, delayReveal);
            tl.to(revealPr, { value: 1, duration: 1, ease: 'sine.out' }, delayReveal);
            tl.fromTo(
                uShineWipePr,
                { value: 0 },
                { value: 1, duration: 1.2, ease: 'sine.out' },
                delayReveal + 0.4
            );
            tl.add(() => cardMotion.playEffect('spin'), delayReveal);
            // tl.add(() => showShineWipe(), delayReveal + 0.5);

            // cardMotion.playEffect('spin'); // 'spin' | 'wiggle' | 'tilt' | 'zoom'
        } else {
            // cardMotion.playEffect('bounce');
            showShineWipe();
        }

        wipeAngle.value = BASE_RAW_WIPE_ANGLE;

        if (fanatics.fgDimmed.value) {
            fanatics.fgDimmed.value = false;
            revealFinal.value = true;
        }
    };

    const showShineWipe = ({ delay = 0, useBaseAngle = false } = {}) => {
        const angle = randomFloat(0, 360);
        const { uShineWipeAngle, uShineWipePr } = shineWipe.refs;
        uShineWipeAngle.value = useBaseAngle ? BASE_RAW_WIPE_ANGLE : angle;

        gsap.killTweensOf(uShineWipePr);
        gsap.fromTo(
            uShineWipePr,
            { value: 0 },
            { value: 1, duration: 1, delay, ease: 'power1.out' }
        );
    };

    const revealFast = () => {
        revealPr.value = 1;
        revealFinal.value = true;
    };

    const setRender = () => {};

    const onUpdate = ({ timestamp }) => {
        cardMotion.update(timestamp);
        time.value = clock.getElapsed();
        depthOffset.value = map(object.position.z, -1, 0, 1, 0, true);

        shaders.forEach((shader) => {
            shader.uniforms.uTime.value = time.value;
        });
    };

    const setRotation = (val) => {
        cardMotion.setRotation(val);
        if (props.motionActive) audio.play('cardRotate');
    };

    const setFlip = (val) => {
        cardMotion.setFlip(val);
        if (props.motionActive) audio.play('cardFlip');
    };

    const setZoom = (val) => {
        if (val && cardMotion.isZooming.value) {
            logger.debug('CardRaw manual zoom skipped because card is already zooming');
            return;
        }

        cardMotion.setZoomManual(val ? 1 : 0);
    };

    const setInteractive = (val) => {
        internalInteractive.value = val;

        if (val) {
            addCursor('grab');
        } else {
            removeCursor('grab');
        }
    };

    const resetInteraction = () => {
        cardMotion.resetInteractionMotion(true);
    };

    const reset = () => {
        setRotation(0);
        setZoom(zoomed.value);
        setFlip(false);

        inProgress.value = 0;
        inActivePosition.value = false;

        revealFinal.value = false;
        revealedComplete.value = false;
        propsTransition.transitionRevealId = null;

        // need to wait for the card to be zoom out
        nextTick(() => cardMotion.reset());
    };

    /*     const getRevealTransitionId = ({ id = null } = {}) => {
        const baseTransitions = [
                        TRANSITIONS_REVEAL.SPIN,
            TRANSITIONS_REVEAL.TILT,
            TRANSITIONS_REVEAL.WIGGLE,
            TRANSITIONS_REVEAL.ZOOM,
        ];

        return id || baseTransitions[randomInt(0, baseTransitions.length - 1)];
    }; */

    useRafBool(render, onUpdate);

    onBeforeUnmount(() => {
        disposeObject(object);
    });

    // Update static material properties (only re-runs when pane values change)
    watchEffect(() => {
        shaders.forEach((shader) => {
            if ('metalness' in shader) shader.metalness = metalness.value;
            if ('roughness' in shader) shader.roughness = roughness.value;
            if ('envMapIntensity' in shader) shader.envMapIntensity = envMapIntensity.value;
            shader.uniforms.uOutlineScale.value = outlineScale.value;
            if (shader.envMapRotation) {
                shader.envMapRotation.set(
                    envMapRotation.value.x,
                    envMapRotation.value.y,
                    envMapRotation.value.z
                );
            }
            shader.uniforms.u_matcapScale.value = matcapScale.value;
            shader.uniforms.uShineZOffset.value = shineZOffset.value;
        });
    });

    // Update animated progress uniform (separate to avoid re-running static updates)
    watchEffect(() => {
        shadersAll.forEach((shader) => {
            if (!shader.uniforms.progress) return;
            shader.uniforms.progress.value = inProgress.value;
        });
    });

    provide('card', {
        inProgress,
        revealPr,
        time,
        depthOffset,
    });

    watch(render, setRender);
    watch(visible, setVisible);
    watch(active, setActive);
    watch(revealed, setRevealed);

    watch(() => (canRotate.value ? props.rotation : 0), setRotation);
    watch(canFlip, setFlip);
    watch(zoomed, setZoom);
    watch(() => props.interactive, setInteractive);
    watch(cardMotion.isZooming, (val) => emit('zooming', val));

    const options = { min: 0, max: 1, step: 0.01 };

    watchEffect(() => {
        const {
            uShineWipeAngle,
            uShineWipePr,
            uShineWipeThickness,
            uShineWipeIntensity,
            uShineWipeLightTint,
        } = shineWipe.refs;

        shaders.forEach((shader) => {
            shader.uniforms.uWipeAngle.value = wipeAngle.value;
            shader.uniforms.uWipeRevealPr.value = revealPr.value;

            shader.uniforms.uShineWipeAngle.value = wipeAngle.value;
            shader.uniforms.uShineWipePr.value = uShineWipePr.value;
            shader.uniforms.uShineWipeThickness.value = uShineWipeThickness.value;
            shader.uniforms.uShineWipeIntensity.value = uShineWipeIntensity.value;
            shader.uniforms.uShineWipeLightTint.value = uShineWipeLightTint.value;
        });
    });

    // watch(wipeAngle, (val) => {
    //     shineWipeAngle.value = val;
    // });

    const emit = defineEmits(['revealStart', 'revealComplete', 'zooming']);

    const pane = usePane(
        [
            { name: 'revealPr', value: revealPr, options },
            { name: 'wipeAngle', value: wipeAngle, options: { min: 0, max: 360, step: 0.1 } },
            { name: 'inProgress', value: inProgress, options },
            { name: 'metalness', value: metalness, options },
            { name: 'roughness', value: roughness, options },
            { name: 'envMapRotation', value: envMapRotation },
            {
                name: 'envMapIntensity',
                value: envMapIntensity,
                options: { min: 0, max: 10, step: 0.1 },
            },
            { name: 'matcapScale', value: matcapScale, options: { min: 0, max: 10, step: 0.1 } },
            {
                name: 'outlineScale',
                value: outlineScale,
                options: { min: 0.5, max: 1, step: 0.001 },
            },
            { name: 'shineZOffset', value: shineZOffset, options: { min: -5, max: 5, step: 0.01 } },
        ],
        { title: 'CardRaw', subfolder: true, expanded: false }
    );
    /*     pane?.addBlade({
        view: 'list',
        label: 'Post Reveal Transition',
        options: Object.keys(TRANSITIONS_REVEAL).map((key) => ({
            text: key,
            value: TRANSITIONS_REVEAL[key],
        })),
        value: propsTransition.transitionRevealId,
    }).on('change', (e) => (propsTransition.transitionRevealId = e.value)); */

    defineExpose({
        show,
        hide,
        fadeIn,
        fadeOut,
        setInProgress,
        reset,
        object,
        isBaseTier,
        isHighTier,
        revealFast,
        showShineWipe,
        resetInteraction,
        isZooming: cardMotion.isZooming,
        zoomPr: cardMotion.zoomPr,
        setCardDetailsActive,
        // paletteId,
    });
</script>
